import { useMemo, useState } from 'react';
import { Search, ArrowDownToLine, ArrowUpFromLine, Scale } from 'lucide-react';
import { Input } from '../ui/input';
import { SimpleTable } from './K2Tables';
import { fmtCz, fmtKc, pctChange } from './k2utils';

const COLUMNS = [
  { key: 'date', label: 'Datum', render: (r) => fmtCz(r.date), className: 'whitespace-nowrap' },
  { key: 'doc', label: 'Doklad', className: 'font-medium whitespace-nowrap' },
  { key: 'direction', label: 'Směr', render: (r) => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.direction === 'in' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
      {r.direction === 'in' ? 'Přibylo' : 'Ubylo'}
    </span>
  ) },
  { key: 'route', label: 'Převod', render: (r) => `${r.from} → ${r.to}`, className: 'whitespace-nowrap' },
  { key: 'description', label: 'Popis', className: 'text-[#57534E]' },
  { key: 'item_count', label: 'Položek', num: true },
  { key: 'value', label: 'Hodnota', num: true, render: (r) => fmtKc(r.value), className: 'font-semibold' },
];

const sums = (rows, from, to) => {
  const r = rows.filter((x) => x.date >= from && x.date <= to);
  const inV = r.filter((x) => x.direction === 'in').reduce((a, x) => a + x.value, 0);
  const outV = r.filter((x) => x.direction === 'out').reduce((a, x) => a + x.value, 0);
  return { inV, outV, net: inV - outV };
};

const Tile = ({ icon: Icon, label, value, prev, showPrev, color, testId }) => {
  const p = pctChange(value, prev);
  return (
    <div className="p-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAF8]" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#57534E]">
        <Icon className="w-4 h-4" style={{ color }} /> {label}
      </div>
      <div className="mt-1 text-xl font-bold text-[#052E16] font-['Manrope'] tabular-nums" data-testid={`${testId}-value`}>{fmtKc(value)}</div>
      {showPrev && (
        <div className="text-xs text-[#57534E] mt-0.5" data-testid={`${testId}-prev`}>
          předch. {fmtKc(prev)}{p != null && <span className="ml-1 font-semibold">({p >= 0 ? '+' : ''}{p.toFixed(1)} %)</span>}
        </div>
      )}
    </div>
  );
};

export const K2NesMovesSection = ({ transfers, range, withPrev }) => {
  const [q, setQ] = useState('');
  const cur = sums(transfers, range.from, range.to);
  const prev = sums(transfers, range.prevFrom, range.prevTo);
  const rows = useMemo(() => {
    const ql = q.toLowerCase();
    return transfers.filter((x) => x.date >= range.from && x.date <= range.to)
      .filter((x) => !ql || `${x.doc} ${x.description} ${x.from} → ${x.to}`.toLowerCase().includes(ql));
  }, [transfers, range, q]);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-sm space-y-4" data-testid="k2-nes-moves-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-bold text-[#052E16] font-['Manrope']">Sklad NES – pohyb v období</h2>
          <p className="text-xs text-[#57534E]">Převodky do NES a z NES na jiné sklady (přesuny v rámci NES se nepočítají)</p>
        </div>
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat…" className="pl-9" data-testid="k2-nes-moves-search" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile icon={ArrowDownToLine} label="Přibylo" value={cur.inV} prev={prev.inV} showPrev={withPrev} color="#DC2626" testId="k2-nes-in" />
        <Tile icon={ArrowUpFromLine} label="Ubylo" value={cur.outV} prev={prev.outV} showPrev={withPrev} color="#16A34A" testId="k2-nes-out" />
        <Tile icon={Scale} label="Změna stavu" value={cur.net} prev={prev.net} showPrev={withPrev} color="#F59E0B" testId="k2-nes-net" />
      </div>
      <SimpleTable key={q} testId="k2-nes-moves-table" columns={COLUMNS} rows={rows} rowKey={(r) => `${r.rid}-${r.direction}`} />
    </div>
  );
};
